import { useState } from "react"
import { createPortal } from "react-dom"
import EventPopover from "../components/EventPopover"
import type { CalendarEvent } from "../models/calendar.model"

interface PopoverState {
  event: CalendarEvent
  position: { top: number; left: number }
}

const POPOVER_WIDTH = 320
const POPOVER_HEIGHT = 340

export function useEventPopover() {
  const [state, setState] = useState<PopoverState | null>(null)

  const closePopover = () => setState(null)

  const openPopover = (event: CalendarEvent, target: HTMLElement, offset = 8) => {
    if (typeof window === "undefined") return
    const rect = target.getBoundingClientRect()
    const scrollY = window.scrollY
    const scrollX = window.scrollX
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    const desiredTop = rect.bottom + scrollY + offset
    const maxTop = scrollY + viewportHeight - POPOVER_HEIGHT - 16
    const minTop = scrollY + 16
    const top = Math.max(minTop, Math.min(maxTop, desiredTop))

    let left = rect.left + scrollX
    if (left + POPOVER_WIDTH + 16 > scrollX + viewportWidth) {
      left = scrollX + viewportWidth - POPOVER_WIDTH - 16
    }
    left = Math.max(scrollX + 16, left)

    setState({ event, position: { top, left } })
  }

  const popoverPortal =
    typeof document !== "undefined" && state
      ? createPortal(
          <div className="fixed inset-0 z-[120]" onClick={closePopover}>
            <div className="absolute inset-0" />
            <div
              className="absolute"
              style={{ top: state.position.top, left: state.position.left }}
              onClick={e => e.stopPropagation()}
            >
              <EventPopover event={state.event} onClose={closePopover} />
            </div>
          </div>,
          document.body
        )
      : null

  return {
    activeEventId: state?.event.id ?? null,
    openPopover,
    closePopover,
    popoverPortal,
  }
}
